// src/components/gallery/PhotoGallery.tsx
// Komponen galeri foto dengan grid responsif dan modal

import React, { useState } from 'react';
import { Search, Grid, List, Camera, Share2 } from 'lucide-react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

import SharePopup from './SharePopup';
import { useProfileData } from '../../hooks/useProfileData';
import { useSocialLinks } from '../../hooks/useSocialLinks';

const PhotoGallery: React.FC = () => {
  const { profileData, loading, error } = useProfileData();
  const { socialLinks } = useSocialLinks();

  const instagramHandle = socialLinks?.instagram
    ? '@' + socialLinks.instagram.replace(/^https?:\/\/(www\.)?instagram\.com\//i, '').replace(/\/$/, '')
    : '@evano.rama';

  const rawImages = profileData?.gallery || [];

  // Urutkan galeri: foto terbaru berada di atas
  const images = [...rawImages].sort((a: any, b: any) => {
    const timeA = new Date(a.created_at || a.date || 0).getTime();
    const timeB = new Date(b.created_at || b.date || 0).getTime();
    if (timeB !== timeA) return timeB - timeA;
    return (b.sort_order || 0) - (a.sort_order || 0);
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'masonry'>('grid');
  const [sharePopup, setSharePopup] = useState<{
    isOpen: boolean;
    imageUrl: string;
    imageTitle: string;
    imageDescription?: string;
  }>({ isOpen: false, imageUrl: '', imageTitle: '', imageDescription: '' });

  // Filter images berdasarkan search
  const filteredImages = images.filter(image => {
    const matchesSearch = (image.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (image.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });



  // Handle share popup
  const handleShare = (image: any, event: React.MouseEvent) => {
    event.stopPropagation();
    setSharePopup({
      isOpen: true,
      imageUrl: image.image_url,
      imageTitle: image.title,
      imageDescription: image.description
    });
  };

  const handleCloseSharePopup = () => {
    setSharePopup({ isOpen: false, imageUrl: '', imageTitle: '', imageDescription: '' });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 space-y-4 md:space-y-0">
          <div className="h-8 bg-gray-200 rounded w-32 animate-pulse"></div>
          <div className="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>
        </div>
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="h-10 bg-gray-200 rounded flex-1 animate-pulse"></div>
          <div className="h-10 bg-gray-200 rounded w-40 animate-pulse"></div>
          <div className="h-10 bg-gray-200 rounded w-20 animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="h-72 bg-gray-200 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Galeri Foto</h2>
        <div className="text-center py-12">
          <Camera className="h-16 w-16 text-red-300 mx-auto mb-4" />
          <p className="text-red-500 text-lg">Error memuat data galeri: {error}</p>
        </div>
      </div>
    );
  }

  if (!images || images.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Galeri Foto</h2>
        <div className="text-center py-12">
          <Camera className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">Belum ada foto yang ditambahkan</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 md:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Galeri Foto</h2>
        <div className="flex items-center text-xs sm:text-sm text-gray-500">
          <Camera className="h-4 w-4 mr-1 text-purple-500" />
          <span>{filteredImages.length} dari {images.length} foto</span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2 sm:gap-4 mb-4 sm:mb-8">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Cari foto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 border border-gray-300 rounded-xl text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          />
        </div>

        {/* View Mode Toggle */}
        <div className="flex border border-gray-300 rounded-xl overflow-hidden flex-shrink-0">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 ${
              viewMode === 'grid'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            } transition-colors cursor-pointer`}
            title="Grid View"
          >
            <Grid className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('masonry')}
            className={`p-2 ${
              viewMode === 'masonry'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            } transition-colors cursor-pointer`}
            title="Masonry View"
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* No Results */}
      {filteredImages.length === 0 && (
        <div className="text-center py-12">
          <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Tidak ada foto yang sesuai dengan pencarian</p>
        </div>
      )}

      {/* Gallery Grid with PhotoView */}
      {filteredImages.length > 0 && (
        <PhotoProvider
          speed={() => 800}
          easing={(type) => (type === 2 ? 'cubic-bezier(0.36, 0, 0.66, -0.56)' : 'cubic-bezier(0.34, 1.56, 0.64, 1)')}
          toolbarRender={() => {
            return null; // Hanya menampilkan tombol close default
          }}
        >
          <div className={`grid gap-2.5 sm:gap-6 ${
            viewMode === 'grid' 
              ? 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4' 
              : 'columns-2 sm:columns-2 md:columns-3 lg:columns-4'
          }`}>
            {filteredImages.map((image, index) => (
              <div key={index} className="relative group cursor-pointer">
                <PhotoView src={image.image_url}>
                  <div className="relative overflow-hidden rounded-xl shadow-xs sm:shadow-lg hover:shadow-xl transition-all duration-300 transform sm:hover:scale-105">
                    <img
                      src={image.image_url}
                      alt={image.title}
                      className="w-full h-40 sm:h-72 object-cover"
                      loading="lazy"
                    />
                    {/* Bottom gradient overlay with title */}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2 sm:p-3 text-white transition-opacity duration-300">
                      <p className="font-semibold text-xs sm:text-sm truncate drop-shadow-sm">{image.title}</p>
                    </div>
                  </div>
                </PhotoView>
                
                {/* Share Button */}
                <button
                  onClick={(e) => handleShare(image, e)}
                  className="absolute top-2 right-2 sm:top-3 sm:right-3 p-1.5 sm:p-2 bg-white/90 hover:bg-white rounded-full shadow-md opacity-90 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-200 z-10 cursor-pointer active:scale-90"
                  title="Bagikan foto"
                >
                  <Share2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-700" />
                </button>
              </div>
            ))}
          </div>
        </PhotoProvider>
      )}



      {/* Share Popup */}
      <SharePopup
        isOpen={sharePopup.isOpen}
        onClose={handleCloseSharePopup}
        imageUrl={sharePopup.imageUrl}
        imageTitle={sharePopup.imageTitle}
        imageDescription={sharePopup.imageDescription}
        authorName={profileData?.fullName || 'Evano Rama'}
        authorAvatar={profileData?.profileImageUrl}
        authorHandle={instagramHandle}
      />
    </div>
  );
};

export default PhotoGallery;
